import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { empMedicalService } from '@/services/empMedicalService'
import { employeesService } from '@/services/employeesService'
import type { MedicalVm } from '@/api/types/empMedical.types'
import { showError, showSuccess } from '@/lib/swal'

function toDateInputValue(d?: string): string {
  if (!d) return ''
  try {
    const date = new Date(d)
    return date.toISOString().slice(0, 10)
  } catch {
    return String(d).slice(0, 10)
  }
}

export const MedicalEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<{ id?: number; empCode?: string; empFirstNameEn?: string; empSecondNameEn?: string; empLastNameEn?: string }[]>([])
  const [medicalCompanies, setMedicalCompanies] = useState<{ id?: number; name?: string; lookupNameEn?: string; lookupNameAr?: string }[]>([])
  const [medicalClasses, setMedicalClasses] = useState<{ id?: number; name?: string; lookupNameEn?: string; lookupNameAr?: string }[]>([])

  const [form, setForm] = useState({
    empId: 0,
    medicalClassId: 0,
    medicalCard: '',
    issueDate: '',
    expiryDate: '',
    bloodGroup: '',
    medicalCompanyId: 0,
    medicalDetail: '',
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        const data = (res as any)?.data ?? (res as any)?.Data ?? res
        setEmployees(Array.isArray(data) ? data : [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    empMedicalService.getMedicalCompanies().then((data) => setMedicalCompanies(data ?? [])).catch(() => setMedicalCompanies([]))
    empMedicalService.getMedicalClasses().then((data) => setMedicalClasses(data ?? [])).catch(() => setMedicalClasses([]))
  }, [])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await empMedicalService.getById(Number(id))
        const raw = data as any
        setForm({
          empId: raw.empId ?? raw.employeeId ?? 0,
          medicalClassId: raw.medicalClassId ?? 0,
          medicalCard: raw.medicalCard ?? '',
          issueDate: toDateInputValue(raw.issueDate),
          expiryDate: toDateInputValue(raw.expiryDate),
          bloodGroup: raw.bloodGroup ?? '',
          medicalCompanyId: raw.medicalCompanyId ?? 0,
          medicalDetail: raw.medicalDetail ?? '',
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load medical record.')
        navigate('/medicals')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, navigate])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
        const label = fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? `Emp ${e.id}`
        return { value: String(e.id ?? 0), label }
      }).filter((o) => o.value !== '0'),
    [employees]
  )

  const medicalCompanyOptions = useMemo(
    () =>
      medicalCompanies.map((t) => {
        const name = (t as any).lookupNameEn ?? (t as any).lookupNameAr ?? (t as any).name ?? `Company ${t.id}`
        return { value: String(t.id ?? 0), label: name }
      }),
    [medicalCompanies]
  )

  const medicalClassOptions = useMemo(
    () =>
      medicalClasses.map((t) => {
        const name = (t as any).lookupNameEn ?? (t as any).lookupNameAr ?? (t as any).name ?? `Class ${t.id}`
        return { value: String(t.id ?? 0), label: name }
      }),
    [medicalClasses]
  )

  const handleChange = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error('Please select a branch.')
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error('Please select an employee.')
      return
    }
    if (!form.medicalClassId || form.medicalClassId === 0) {
      toast.error('Insurance Class Type is required.')
      return
    }
    if (!form.medicalCard.trim()) {
      toast.error('Medical Card is required.')
      return
    }
    if (!form.bloodGroup.trim()) {
      toast.error('Blood Group is required.')
      return
    }
    if (!form.medicalCompanyId || form.medicalCompanyId === 0) {
      toast.error('Medical Company is required.')
      return
    }
    if (!form.medicalDetail.trim()) {
      toast.error('Medical Detail is required.')
      return
    }

    const payload: MedicalVm = {
      ...(isEditing && id ? { id: Number(id) } : {}),
      empId: form.empId,
      employeeId: form.empId,
      medicalClassId: form.medicalClassId,
      medicalCard: form.medicalCard.trim(),
      issueDate: form.issueDate || undefined,
      expiryDate: form.expiryDate || undefined,
      bloodGroup: form.bloodGroup.trim(),
      medicalCompanyId: form.medicalCompanyId,
      medicalDetail: form.medicalDetail.trim(),
      branchId,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await empMedicalService.update(payload)
        await showSuccess('Updated', 'Medical record updated successfully.')
      } else {
        await empMedicalService.save(payload)
        await showSuccess('Saved', 'Medical record saved successfully.')
      }
      navigate('/medicals')
    } catch (e: any) {
      await showError('Save failed', e?.response?.data?.message ?? e?.message ?? 'Failed to save medical record.')
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Medicals" description="Add or edit medical record." />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">Please select a branch first.</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Medicals"
        description={isEditing ? 'Edit medical record' : 'Add new medical record'}
      />

      <Card>
        <CardHeader>
          <CardTitle>Medicals</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select employee' }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => handleChange({ empId: Number(v) || 0 })}
                placeholder="Select employee"
                searchPlaceholder="Search employee..."
                emptyMessage="No employee found"
                disabled={isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label>Insurance Class Type *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select class' }, ...medicalClassOptions]}
                value={form.medicalClassId ? String(form.medicalClassId) : '0'}
                onValueChange={(v) => handleChange({ medicalClassId: Number(v) || 0 })}
                placeholder="Select insurance class"
                searchPlaceholder="Search..."
                emptyMessage="No class found"
              />
            </div>
            <div className="space-y-2">
              <Label>Medical Card *</Label>
              <Input
                value={form.medicalCard}
                onChange={(e) => handleChange({ medicalCard: e.target.value })}
                placeholder="Medical card"
              />
            </div>
            <div className="space-y-2">
              <Label>Issue Date</Label>
              <Input
                type="date"
                value={form.issueDate}
                onChange={(e) => handleChange({ issueDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={form.expiryDate}
                onChange={(e) => handleChange({ expiryDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Blood Group *</Label>
              <Input
                value={form.bloodGroup}
                onChange={(e) => handleChange({ bloodGroup: e.target.value })}
                placeholder="Blood group"
              />
            </div>
            <div className="space-y-2">
              <Label>Medical Company *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select company' }, ...medicalCompanyOptions]}
                value={form.medicalCompanyId ? String(form.medicalCompanyId) : '0'}
                onValueChange={(v) => handleChange({ medicalCompanyId: Number(v) || 0 })}
                placeholder="Select medical company"
                searchPlaceholder="Search..."
                emptyMessage="No company found"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Medical Detail *</Label>
            <Textarea
              value={form.medicalDetail}
              onChange={(e) => handleChange({ medicalDetail: e.target.value })}
              placeholder="Medical detail"
              rows={4}
              className="resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/medicals')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
